import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { Empresa } from '../../models/empresa.model';
import { EmpresaService } from '../../services/empresa.service';
import { ToastComponent } from '../../components/toast/toast.component';
import { ToastService } from '../../services/toast.service';

@Component({
  selector: 'app-licencias',
  imports: [CommonModule, FormsModule, ToastComponent],
  templateUrl: './licencias.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './licencias.component.css',
})
export class LicenciasComponent implements OnInit, OnDestroy {
  private readonly empresaService = inject(EmpresaService);
  private readonly toastService = inject(ToastService);
  private readonly destroy$ = new Subject<void>();

  empresas: Empresa[] = [];
  filteredEmpresas: Empresa[] = [];

  loading = false;
  error = '';
  searchTerm = '';
  showRenewModal = false;
  selectedEmpresa: Empresa | null = null;
  renewalDate = '';

  ngOnInit(): void {
    this.loadLicencias();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadLicencias(): void {
    this.loading = true;
    this.error = '';

    this.empresaService
      .getEmpresas()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (empresas) => {
          this.empresas = empresas;
          this.applyFilter();
          this.loading = false;
        },
        error: (error) => {
          this.error = error?.message || 'No se pudieron cargar las licencias';
          this.loading = false;
        },
      });
  }

  applyFilter(): void {
    const search = this.searchTerm.trim().toLowerCase();

    this.filteredEmpresas = this.empresas
      .filter((empresa) => !!empresa.licencia)
      .filter((empresa) => {
        if (!search) {
          return true;
        }

        return (
          empresa.nombre?.toLowerCase().includes(search) ||
          empresa.ruc?.toLowerCase().includes(search)
        );
      })
      .sort((a, b) => {
        const dateA = new Date(a.licencia?.fecha_fin || 0).getTime();
        const dateB = new Date(b.licencia?.fecha_fin || 0).getTime();
        return dateA - dateB;
      });
  }

  openRenewModal(empresa: Empresa): void {
    this.selectedEmpresa = empresa;
    const currentEndDate = this.toInputDate(empresa.licencia?.fecha_fin);
    this.renewalDate = this.addMonths(
      currentEndDate || this.toInputDate(new Date()),
      1,
    );
    this.showRenewModal = true;
  }

  closeRenewModal(): void {
    this.showRenewModal = false;
    this.selectedEmpresa = null;
    this.renewalDate = '';
  }

  confirmRenewal(): void {
    if (!this.selectedEmpresa) {
      return;
    }

    if (!this.renewalDate) {
      return;
    }

    if (!/^\d{4}-\d{2}-\d{2}$/.test(this.renewalDate)) {
      this.toastService.show({
        text: 'Formato inválido. Usa YYYY-MM-DD.',
        classname: 'bg-danger text-light',
        delay: 4000,
      });
      return;
    }

    this.empresaService
      .renovarLicencia(
        this.selectedEmpresa.id,
        this.toApiDateTime(this.renewalDate),
      )
      .subscribe({
        next: () => {
          this.toastService.show({
            text: `Licencia renovada para ${this.selectedEmpresa?.nombre}`,
            classname: 'bg-success text-light',
            delay: 3000,
          });
          this.closeRenewModal();
          this.loadLicencias();
        },
        error: (error) => {
          this.toastService.show({
            text: error?.message || 'No se pudo renovar la licencia',
            classname: 'bg-danger text-light',
            delay: 4500,
          });
        },
      });
  }

  getEstado(empresa: Empresa): { label: string; className: string } {
    const endDate = new Date(empresa.licencia?.fecha_fin || 0);
    const now = new Date();
    const days = Math.ceil(
      (endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (!empresa.licencia || Number.isNaN(endDate.getTime())) {
      return {
        label: 'Sin licencia',
        className: 'bg-slate-200 text-slate-700',
      };
    }

    if (days < 0) {
      return { label: 'Vencida', className: 'bg-red-100 text-red-700' };
    }

    if (days <= 15) {
      return { label: 'Por vencer', className: 'bg-amber-100 text-amber-800' };
    }

    return { label: 'Activa', className: 'bg-emerald-100 text-emerald-700' };
  }

  private toInputDate(value: unknown): string {
    if (typeof value === 'string') {
      const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        return `${match[1]}-${match[2]}-${match[3]}`;
      }
    }

    const date = new Date(value as string | number | Date);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private addMonths(dateStr: string, monthsToAdd: number): string {
    const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) {
      return '';
    }

    const year = Number(match[1]);
    const monthIndex = Number(match[2]) - 1;
    const day = Number(match[3]);

    const targetMonthRaw = monthIndex + monthsToAdd;
    const targetYear = year + Math.floor(targetMonthRaw / 12);
    const targetMonth = ((targetMonthRaw % 12) + 12) % 12;

    const lastDayInTargetMonth = new Date(
      targetYear,
      targetMonth + 1,
      0,
    ).getDate();
    const targetDay = Math.min(day, lastDayInTargetMonth);

    return `${targetYear}-${String(targetMonth + 1).padStart(2, '0')}-${String(targetDay).padStart(2, '0')}`;
  }

  private toApiDateTime(dateStr: string): string {
    // Use noon UTC to avoid date rollback when backend/client run in different timezones.
    return `${dateStr}T12:00:00.000Z`;
  }
}
